import { useState } from 'react'
import { useEvent } from '@rc-component/util'
import { Anchor, Button, theme, Tooltip } from 'antd'
import { Compass, PanelRightClose, PanelRightOpen } from 'lucide-react'
import { cn } from '@/utils/index.ts'

export type FormAnchorNavSection = {
  id: string
  title: string
}

type FormAnchorNavProps = {
  sections: FormAnchorNavSection[]
  className?: string
  sectionPrefix?: string
}

export function FormAnchorNav({ sections, className, sectionPrefix = 'section' }: FormAnchorNavProps) {
  const { token } = theme.useToken()
  const [collapsed, setCollapsed] = useState(false)
  const firstAnchor = sections[0] ? `#${sectionPrefix}-${sections[0].id}` : ''
  const getCurrentAnchor = useEvent((currentAnchor: string) => currentAnchor || firstAnchor)

  if (sections.length === 0) {
    return null
  }

  return (
    <aside
      aria-label="表单导航"
      className={cn(
        'sticky top-20 max-h-[calc(100vh-8rem)] shrink-0 overflow-y-auto transition-[width] duration-300',
        collapsed ? 'w-10' : 'w-40',
        className
      )}
    >
      {collapsed ? (
        <Tooltip placement="left" title="展开表单导航">
          <Button
            aria-label="展开表单导航"
            icon={<PanelRightOpen aria-hidden size={16} />}
            onClick={() => setCollapsed(false)}
            size="small"
            type="text"
          />
        </Tooltip>
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between gap-2 px-1">
            <div className="flex min-w-0 items-center gap-2">
              <Compass aria-hidden size={15} style={{ color: token.colorTextSecondary }} />
              <span className="truncate text-sm" style={{ color: token.colorTextSecondary }}>
                表单导航
              </span>
            </div>
            <Tooltip placement="left" title="折叠表单导航">
              <Button
                aria-label="折叠表单导航"
                icon={<PanelRightClose aria-hidden size={16} />}
                onClick={() => setCollapsed(true)}
                size="small"
                type="text"
              />
            </Tooltip>
          </div>
          <Anchor
            affix={false}
            items={sections.map((section) => ({
              href: `#${sectionPrefix}-${section.id}`,
              key: section.id,
              title: section.title
            }))}
            getCurrentAnchor={getCurrentAnchor}
            replace
            targetOffset={96}
          />
        </>
      )}
    </aside>
  )
}
